import { useMutation, useQuery } from "@tanstack/react-query";
import newApi from "src/newApi";
import { queryClient } from "src/queryClient";
import { getService } from "../helpers/isPes";
import options from "./defaultOptions";

const REVIEWS_MAX = 100;

const aiQuestionDetailsKey = (questionId: number | string) => [
	"ai-question-details",
	getService(),
	questionId,
];

interface ToggleAiResponseRateVariables {
	like: boolean;
	id: number | string;
}

interface ToggleAiResponseApprovalVariables {
	id: number | string;
	questionId?: number | string;
}

interface UpdateAiResponseContentVariables {
	id: number | string;
	response: string;
	questionId?: number | string;
}

interface CreateAiResponseReviewVariables {
	id: number | string;
	review: string;
}

interface UpdateAiReviewStatusVariables {
	reviewId: number | string;
	status: string;
}

export function useGenerateAIQuestionDetails(questionId: number | string) {
	return useQuery({
		queryKey: aiQuestionDetailsKey(questionId),
		queryFn: async () => {
			const result = await newApi.generateAiQuestionDetails(questionId);
			return result?.data ?? null;
		},
		...options,
		enabled: !!questionId,
		staleTime: Infinity,
	});
}

export function useGetAiChatResponses({
	promptType,
	itemType,
	itemId,
}: {
	promptType: string;
	itemType: string;
	itemId: number | string;
}) {
	return useQuery({
		queryKey: ["ai-chat-response-rate", promptType, itemType, itemId],

		queryFn: () => {
			return newApi.getAiChatResponses({ promptType, itemType, itemId });
		},

		...options,
		staleTime: 0,
	});
}

export function useToggleAIResponseRate() {
	return useMutation<unknown, unknown, ToggleAiResponseRateVariables>({
		mutationFn: ({ like, id }) => {
			return newApi.toggleAiResponseRate(id, like);
		},
		onSuccess: async (_data, { id }) => {
			await queryClient.refetchQueries({ queryKey: ["ai-response-rate", id] });
		},
	});
}

export function useGetAIResponseRate(id: number | string) {
	return useQuery({
		queryKey: ["ai-response-rate", id],

		queryFn: () => {
			return newApi.getAiResponseRate(id);
		},

		...options,
	});
}

export function useToggleAiResponseApproval() {
	return useMutation<unknown, unknown, ToggleAiResponseApprovalVariables>({
		mutationFn: ({ id, questionId: _questionId }) =>
			newApi.toggleAiResponseApproval(id),
		onSuccess: async (_data, { id, questionId }) => {
			await Promise.all([
				queryClient.invalidateQueries({
					queryKey: ["ai-response-reviews", getService(), id],
				}),
				questionId
					? queryClient.invalidateQueries({
							queryKey: aiQuestionDetailsKey(questionId),
						})
					: Promise.resolve(),
			]);
		},
	});
}

export function useUpdateAiResponseContent() {
	return useMutation<unknown, unknown, UpdateAiResponseContentVariables>({
		mutationFn: ({ id, response, questionId: _questionId }) =>
			newApi.updateAiResponseContent(id, response),
		onSuccess: async (_data, { id, questionId }) => {
			await Promise.all([
				queryClient.invalidateQueries({
					queryKey: ["ai-response-context", getService(), id],
				}),
				queryClient.invalidateQueries({
					queryKey: ["ai-response-reviews", getService(), id],
				}),
				questionId
					? queryClient.invalidateQueries({
							queryKey: aiQuestionDetailsKey(questionId),
						})
					: Promise.resolve(),
			]);
		},
	});
}

export function useAiResponseContext(
	id: number | string,
	params: { filter?: unknown; enabled?: boolean } = {},
) {
	return useQuery({
		queryKey: ["ai-response-context", getService(), id, params.filter],
		queryFn: () => newApi.getAiResponseContext(id, { filter: params.filter }),
		...options,
		enabled: !!id && params.enabled !== false,
		staleTime: Infinity,
	});
}

export function useCreateAiResponseReview() {
	return useMutation<unknown, unknown, CreateAiResponseReviewVariables>({
		mutationFn: ({ id, review }) => newApi.createAiResponseReview(id, review),
		onSuccess: async (_data, { id }) => {
			await queryClient.invalidateQueries({
				queryKey: ["ai-response-reviews", getService(), id],
			});
		},
	});
}

export function useUpdateAiReviewStatus() {
	return useMutation<unknown, unknown, UpdateAiReviewStatusVariables>({
		mutationFn: ({ reviewId, status }) =>
			newApi.updateAiReviewStatus(reviewId, status),
		onSuccess: async () => {
			await queryClient.invalidateQueries({
				queryKey: ["ai-response-reviews", getService()],
			});
		},
	});
}

export function useAiResponseReviews(
	id: number | string,
	params: {
		status?: string;
		sortBy?: string;
		sortOrder?: string;
		enabled?: boolean;
	} = {},
) {
	return useQuery({
		queryKey: [
			"ai-response-reviews",
			getService(),
			id,
			params.status,
			params.sortBy,
			params.sortOrder,
		],

		queryFn: () =>
			newApi.getAiResponseReviews(id, {
				status: params.status,
				sortBy: params.sortBy,
				sortOrder: params.sortOrder,
				limit: REVIEWS_MAX,
			}),

		...options,
		enabled: !!id && params.enabled !== false,
	});
}
